import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { trackPageView } from './services/ga4Analytics';

// Import pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import QuoteBuilder from './pages/QuoteBuilder';
import ProductManager from './pages/ProductManager';
import ProfileSettings from './pages/ProfileSettings';
import Analytics from './pages/Analytics';
import ClientQuoteView from './pages/ClientQuoteView';
import AuthCallback from './pages/AuthCallback';
import SubscriptionOnboarding from './pages/SubscriptionOnboarding';
import OnboardingWelcome from './pages/OnboardingWelcome';
import OnboardingBusinessSetup from './pages/OnboardingBusinessSetup';
import OnboardingTrialSetup from './pages/OnboardingTrialSetup';
import OnboardingTrialSuccess from './pages/OnboardingTrialSuccess';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DemoQuote from './pages/DemoQuote';
import Blog from './pages/Blog';
import BlogIndexRedirect from './pages/BlogIndexRedirect';
import BlogArticle from './pages/BlogArticle';
import HVACQuotingSoftware from './pages/HVACQuotingSoftware';
import PainterQuotingSoftware from './pages/PainterQuotingSoftware';
import WebDeveloperQuotingSoftware from './pages/WebDeveloperQuotingSoftware';
import VideographerQuotingSoftware from './pages/VideographerQuotingSoftware';
import ConstructionQuotingSoftware from './pages/ConstructionQuotingSoftware';
import ElectricalQuotingSoftware from './pages/ElectricalQuotingSoftware';
import ManufacturingQuotingSoftware from './pages/ManufacturingQuotingSoftware';
import PlumbingQuotingSoftware from './pages/PlumbingQuotingSoftware';
import LandscapingQuotingSoftware from './pages/LandscapingQuotingSoftware';
import SmallBusinessQuotingSoftware from './pages/SmallBusinessQuotingSoftware';
import MSPQuotingSoftware from './pages/MSPQuotingSoftware';
import CabinetQuotingSoftware from './pages/CabinetQuotingSoftware';
import WindowDoorQuotingSoftware from './pages/WindowDoorQuotingSoftware';
import SolarQuotingSoftware from './pages/SolarQuotingSoftware';
import AutoBodyQuotingSoftware from './pages/AutoBodyQuotingSoftware';
import SignageQuotingSoftware from './pages/SignageQuotingSoftware';
import RoofingQuotingSoftware from './pages/RoofingQuotingSoftware';
import HandymanQuotingSoftware from './pages/HandymanQuotingSoftware';
import FlooringQuotingSoftware from './pages/FlooringQuotingSoftware';
import AutoDetailingQuotingSoftware from './pages/AutoDetailingQuotingSoftware';
import PestControlQuotingSoftware from './pages/PestControlQuotingSoftware';
import CateringQuotingSoftware from './pages/CateringQuotingSoftware';
import TreeServiceQuotingSoftware from './pages/TreeServiceQuotingSoftware';
import PoolConstructionQuotingSoftware from './pages/PoolConstructionQuotingSoftware';
import InteriorDesignQuotingSoftware from './pages/InteriorDesignQuotingSoftware';
import WeddingPhotographyQuotingSoftware from './pages/WeddingPhotographyQuotingSoftware';
import QuoteVsEstimateGuide from './pages/QuoteVsEstimateGuide';

// Feature Pages
import GoodBetterBestPricing from './pages/features/GoodBetterBestPricing';
import InteractiveProposals from './pages/features/InteractiveProposals';
import AutomatedUpselling from './pages/features/AutomatedUpselling';
import PackagePricing from './pages/features/PackagePricing';
import QuoteTracking from './pages/features/QuoteTracking';

// Template Pages
import HVACTieredProposal from './pages/templates/HVACTieredProposal';
import WebDesignPackages from './pages/templates/WebDesignPackages';
import ConstructionBidOptions from './pages/templates/ConstructionBidOptions';
import PhotographyPackages from './pages/templates/PhotographyPackages';
import LandscapingPackages from './pages/templates/LandscapingPackages';
import AutoDetailingPackages from './pages/templates/AutoDetailingPackages';
import CateringPackages from './pages/templates/CateringPackages';
import ElectricalPackages from './pages/templates/ElectricalPackages';
import InteriorDesignPackages from './pages/templates/InteriorDesignPackages';
import PaintingPackages from './pages/templates/PaintingPackages';
import PestControlPackages from './pages/templates/PestControlPackages';
import PlumbingPackages from './pages/templates/PlumbingPackages';
import PoolConstructionPackages from './pages/templates/PoolConstructionPackages';
import RoofingPackages from './pages/templates/RoofingPackages';
import SolarPackages from './pages/templates/SolarPackages';
import TreeServicePackages from './pages/templates/TreeServicePackages';
import VideographyPackages from './pages/templates/VideographyPackages';

// Comparison Pages
import FlatVsTiered from './pages/compare/FlatVsTiered';
import ExcelVsSoftware from './pages/compare/ExcelVsSoftware';
import QuoteTierVsProposify from './pages/compare/QuoteTierVsProposify';
import QuoteTierVsPandaDoc from './pages/compare/QuoteTierVsPandaDoc';
import QuoteTierVsBetterProposals from './pages/compare/QuoteTierVsBetterProposals';

// Product Pages
import Pricing from './pages/Pricing';
import RoiCalculator from './pages/RoiCalculator';
import RebuildMyQuote from './pages/RebuildMyQuote';
import CaseStudies from './pages/CaseStudies';
import TemplatesHub from './pages/TemplatesHub';
import Examples from './pages/Examples';

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center" role="status" aria-live="polite">
      <div className="spinner spinner-lg mx-auto mb-4" aria-hidden="true"></div>
      <p className="text-gray-700 font-jetbrains">Loading…</p>
      <span className="sr-only">Content is loading</span>
    </div>
  </div>
);

// Simple protected route - just needs authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Main app route - checks onboarding status
const AppRoute = ({ children }) => {
  const { user, loading, profileLoading, isAuthenticated } = useAuth();
  
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  const profile = user?.profile;
  
  // If profile is still loading, show loading screen but don't block forever
  if (profileLoading && !profile) {
    return <LoadingScreen />;
  }
  
  // If we have a user but no profile, provide safe defaults and allow app to work.
  // trial_setup_completed defaults to true under the free-forever model; the page
  // is now an opt-in upgrade path, not a signup gate.
  const safeProfile = profile || {
    onboarding_completed: false,
    trial_setup_completed: true
  };

  if (!safeProfile.onboarding_completed) {
    return <Navigate to="/onboarding/welcome" replace />;
  }

  return children;
};

// Public route - no authentication required
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, profileLoading, user } = useAuth();
  
  if (loading) return <LoadingScreen />;
  
  // Don't redirect during OAuth processing
  if (window.oauthProcessing) {
    return children;
  }
  
  if (isAuthenticated) {
    // Check onboarding status and redirect accordingly
    const profile = user?.profile;
    
    // If profile is still loading, show loading screen but don't block forever
    if (profileLoading && !profile) {
      return <LoadingScreen />;
    }
    
    // Provide safe defaults if profile is missing.
    const safeProfile = profile || {
      onboarding_completed: false,
      trial_setup_completed: true
    };

    if (!safeProfile.onboarding_completed) {
      return <Navigate to="/onboarding/welcome" replace />;
    }

    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  const location = useLocation();

  // Track page views for GA4 and scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(document.title);
  }, [location]);

  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/signup" 
          element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          } 
        />
        
        {/* Client Quote View - Public but doesn't redirect */}
        <Route path="/quote/:uniqueId" element={<ClientQuoteView />} />
        
        {/* Demo Quote View - Public demo quotes for homepage */}
        <Route path="/demo-quote/:industryId" element={<DemoQuote />} />
        
        {/* Auth Callback - Handle OAuth redirects */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Onboarding Flow - Requires auth but not subscription */}
        <Route 
          path="/onboarding/welcome" 
          element={
            <ProtectedRoute>
              <OnboardingWelcome />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/onboarding/business-setup" 
          element={
            <ProtectedRoute>
              <OnboardingBusinessSetup />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/onboarding/trial-setup" 
          element={
            <ProtectedRoute>
              <OnboardingTrialSetup />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/onboarding/trial-success" 
          element={
            <ProtectedRoute>
              <OnboardingTrialSuccess />
            </ProtectedRoute>
          } 
        />
        
        {/* Subscription Onboarding - Requires auth but not subscription */}
        <Route 
          path="/subscription-onboarding" 
          element={
            <ProtectedRoute>
              <SubscriptionOnboarding />
            </ProtectedRoute>
          } 
        />
        
        {/* Legal Pages - Public */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        {/* Redirect SPA /blog -> static /blog/ to keep one canonical source */}
        <Route path="/blog" element={<BlogIndexRedirect />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        
        {/* SEO Landing Pages - Public */}
        <Route path="/hvac-quoting-software" element={<HVACQuotingSoftware />} />
        <Route path="/painting-quote-software" element={<PainterQuotingSoftware />} />
        <Route path="/web-developer-quote-software" element={<WebDeveloperQuotingSoftware />} />
        <Route path="/videographer-quote-software" element={<VideographerQuotingSoftware />} />
        <Route path="/construction-quoting-software" element={<ConstructionQuotingSoftware />} />
        <Route path="/electrical-quoting-software" element={<ElectricalQuotingSoftware />} />
        <Route path="/manufacturing-quoting-software" element={<ManufacturingQuotingSoftware />} />
        <Route path="/plumbing-quoting-software" element={<PlumbingQuotingSoftware />} />
        <Route path="/landscape-quoting-software" element={<LandscapingQuotingSoftware />} />
        <Route path="/small-business-quoting-software" element={<SmallBusinessQuotingSoftware />} />
        <Route path="/msp-quoting-software" element={<MSPQuotingSoftware />} />
        <Route path="/cabinet-quoting-software" element={<CabinetQuotingSoftware />} />
        <Route path="/window-door-quoting-software" element={<WindowDoorQuotingSoftware />} />
        <Route path="/solar-quoting-software" element={<SolarQuotingSoftware />} />
        <Route path="/auto-body-estimating-software" element={<AutoBodyQuotingSoftware />} />
        <Route path="/sign-shop-quoting-software" element={<SignageQuotingSoftware />} />
        <Route path="/roofing-quoting-software" element={<RoofingQuotingSoftware />} />
        <Route path="/handyman-quoting-software" element={<HandymanQuotingSoftware />} />
        <Route path="/flooring-quoting-software" element={<FlooringQuotingSoftware />} />
        <Route path="/auto-detailing-quoting-software" element={<AutoDetailingQuotingSoftware />} />
        <Route path="/pest-control-quoting-software" element={<PestControlQuotingSoftware />} />
        <Route path="/catering-quoting-software" element={<CateringQuotingSoftware />} />
        <Route path="/tree-service-quoting-software" element={<TreeServiceQuotingSoftware />} />
        <Route path="/pool-construction-quoting-software" element={<PoolConstructionQuotingSoftware />} />
        <Route path="/interior-design-quoting-software" element={<InteriorDesignQuotingSoftware />} />
        <Route path="/wedding-photography-quote-software" element={<WeddingPhotographyQuotingSoftware />} />
        
        {/* Educational Guides */}
        <Route path="/quote-vs-estimate" element={<QuoteVsEstimateGuide />} />
        
        {/* Feature Pages */}
        <Route path="/features/good-better-best-pricing-software" element={<GoodBetterBestPricing />} />
        <Route path="/features/interactive-proposals" element={<InteractiveProposals />} />
        <Route path="/features/automated-upselling-tools" element={<AutomatedUpselling />} />
        <Route path="/features/package-pricing-software" element={<PackagePricing />} />
        <Route path="/features/quote-tracking-analytics" element={<QuoteTracking />} />

        {/* Template Pages */}
        <Route path="/templates/tiered-hvac-proposal-template" element={<HVACTieredProposal />} />
        <Route path="/templates/web-design-packages-template" element={<WebDesignPackages />} />
        <Route path="/templates/construction-bid-options-template" element={<ConstructionBidOptions />} />
        <Route path="/templates/photography-pricing-packages" element={<PhotographyPackages />} />
        <Route path="/templates/landscaping-maintenance-packages" element={<LandscapingPackages />} />
        <Route path="/templates/auto-detailing-packages-template" element={<AutoDetailingPackages />} />
        <Route path="/templates/catering-packages-template" element={<CateringPackages />} />
        <Route path="/templates/electrical-packages-template" element={<ElectricalPackages />} />
        <Route path="/templates/interior-design-packages-template" element={<InteriorDesignPackages />} />
        <Route path="/templates/painting-packages-template" element={<PaintingPackages />} />
        <Route path="/templates/pest-control-packages-template" element={<PestControlPackages />} />
        <Route path="/templates/plumbing-packages-template" element={<PlumbingPackages />} />
        <Route path="/templates/pool-construction-packages-template" element={<PoolConstructionPackages />} />
        <Route path="/templates/roofing-packages-template" element={<RoofingPackages />} />
        <Route path="/templates/solar-packages-template" element={<SolarPackages />} />
        <Route path="/templates/tree-service-packages-template" element={<TreeServicePackages />} />
        <Route path="/templates/videography-packages-template" element={<VideographyPackages />} />

        {/* Product Pages */}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/roi" element={<RoiCalculator />} />
        <Route path="/rebuild-my-quote" element={<RebuildMyQuote />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/templates" element={<TemplatesHub />} />
        <Route path="/examples" element={<Examples />} />

        {/* Comparison Pages */}
        <Route path="/compare/flat-rate-vs-tiered-pricing" element={<FlatVsTiered />} />
        <Route path="/compare/excel-vs-quoting-software" element={<ExcelVsSoftware />} />
        <Route path="/compare/quotetier-vs-proposify" element={<QuoteTierVsProposify />} />
        <Route path="/compare/quotetier-vs-pandadoc" element={<QuoteTierVsPandaDoc />} />
        <Route path="/compare/quotetier-vs-better-proposals" element={<QuoteTierVsBetterProposals />} />

        {/* App Routes - Require auth + onboarding */}
        <Route 
          path="/dashboard" 
          element={
            <AppRoute>
              <Dashboard />
            </AppRoute>
          } 
        />
        <Route 
          path="/quote-builder" 
          element={
            <AppRoute>
              <QuoteBuilder />
            </AppRoute>
          } 
        />
        <Route 
          path="/products" 
          element={
            <AppRoute>
              <ProductManager />
            </AppRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <AppRoute>
              <ProfileSettings />
            </AppRoute>
          } 
        />
        <Route 
          path="/analytics" 
          element={
            <AppRoute>
              <Analytics />
            </AppRoute>
          } 
        />
        
        {/* Landing Page */}
        <Route 
          path="/" 
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          } 
        />
        
        {/* 404 - Not Found */}
        <Route 
          path="*" 
          element={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600 mb-6">Page not found</p>
                <a 
                  href="/dashboard" 
                  className="btn-primary inline-block"
                >
                  Go to Dashboard
                </a>
              </div>
            </div>
          } 
        />
      </Routes>
    </div>
  );
}

export default App;
